import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { PageNavigationContext, type DetailTitle } from "@/context/PageNavigation";
import { formatPageTitle, getPageBreadcrumbs } from "@/lib/pageNavigation";

/** One title owner for all routes, including authentication and embedded pages. */
export function PageNavigationLayout() {
  const location = useLocation();
  const breadcrumbs = useMemo(
    () => getPageBreadcrumbs(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const [details, setDetails] = useState<DetailTitle[]>([]);
  const register = useCallback((detail: DetailTitle) => {
    setDetails((current) => {
      const index = current.findIndex((item) => item.id === detail.id);
      if (index < 0) return [...current, detail];
      if (current[index].title === detail.title && current[index].path === detail.path) return current;
      // Updating a loaded name must not move a background drawer above another drawer.
      return current.map((item, i) => i === index ? detail : item);
    });
  }, []);
  const unregister = useCallback((id: string) => {
    setDetails((current) => current.filter((item) => item.id !== id));
  }, []);
  const visibleDetails = details.filter((item) => item.path === location.pathname);
  const activeTitle = visibleDetails[visibleDetails.length - 1]?.title;
  useEffect(() => {
    document.title = formatPageTitle(activeTitle ? [...breadcrumbs, { label: activeTitle }] : breadcrumbs);
  }, [breadcrumbs, activeTitle]);

  const value = useMemo(() => ({
    breadcrumbs, path: location.pathname, register, unregister,
  }), [breadcrumbs, location.pathname, register, unregister]);

  return <PageNavigationContext.Provider value={value}><Outlet /></PageNavigationContext.Provider>;
}
